/**
 * AgentNetworkGuide — tutoriales del recolector de red, dentro de la UI.
 *
 * Responde a las tres dudas que aparecen siempre:
 *   1. ¿Dónde se instala esto? (y la más repetida: «si ya tengo la API key de
 *      UniFi, ¿hace falta instalar algo?» → sí, el colector, pero no SNMP).
 *   2. Cómo se configura SNMP paso a paso, sin tocar el switch a ciegas.
 *   3. Cómo se configura UniFi paso a paso.
 *
 * Se abre como diálogo desde la pestaña Agente → Red. Texto en español a pelo
 * (igual que el resto de la sección del agente) y sin emojis.
 */
import React, { useState } from 'react';
import {
  BookOpenIcon,
  KeyIcon,
  SignalIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';
import Dialog from './common/Dialog';
import { CommandBlock } from './common/Panel';

const TABS = [
  { id: 'start', label: 'Antes de empezar', Icon: BookOpenIcon },
  { id: 'snmp', label: 'SNMP', Icon: SignalIcon },
  { id: 'unifi', label: 'UniFi', Icon: KeyIcon },
  { id: 'ssh', label: 'SSH y problemas', Icon: CommandLineIcon },
];

function H({ children }) {
  return (
    <h4 style={{ margin: '18px 0 8px', fontSize: '0.95rem', color: 'var(--text-primary)' }}>
      {children}
    </h4>
  );
}

function P({ children }) {
  return (
    <p style={{ margin: '0 0 10px', fontSize: '0.86rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
      {children}
    </p>
  );
}

function Steps({ items }) {
  return (
    <ol style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: '0.86rem', lineHeight: 1.65, color: 'var(--text-secondary)' }}>
      {items.map((item, index) => (
        <li key={index} style={{ marginBottom: 8 }}>{item}</li>
      ))}
    </ol>
  );
}

function Note({ children, tone = 'info' }) {
  const border = tone === 'warn' ? 'var(--yellow-600)' : 'var(--accent)';
  return (
    <div
      style={{
        margin: '0 0 14px',
        padding: '10px 12px',
        borderLeft: `3px solid ${border}`,
        background: 'var(--bg-toolbar)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.84rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
}

function Tabla({ rows }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {rows.map(([problema, causa]) => (
        <div
          key={problema}
          style={{
            display: 'flex', gap: 12, padding: '7px 2px',
            borderBottom: '1px solid var(--border)', fontSize: '0.82rem',
          }}
        >
          <span style={{ flex: '1 1 180px', color: 'var(--text-primary)', fontWeight: 500 }}>
            {problema}
          </span>
          <span style={{ flex: '2 1 320px', color: 'var(--text-secondary)' }}>{causa}</span>
        </div>
      ))}
    </div>
  );
}

function StartTab() {
  return (
    <div>
      <P>
        Depende de cómo se lea. Los <strong>controladores por API</strong> (UniFi hoy) los consulta el
        propio servidor del CMDB, así que <strong>no necesitas instalar nada</strong>. Para barrer
        <strong> rangos SNMP/SSH</strong> sí hace falta un pequeño colector dentro de la red del
        cliente: es el mismo agente, ejecutándose con el rol <code>network</code>, y no se inventaría
        a sí mismo.
      </P>

      <H>¿Dónde se instala el colector?</H>
      <P>
        En una máquina de la <strong>VLAN de gestión</strong>, con acceso a los equipos. Vale un
        contenedor Docker, una VM Linux, un mini-PC o una Raspberry Pi. Todas las conexiones salen
        del colector; no hay que abrir ningún puerto de entrada.
      </P>
      <Tabla
        rows={[
          ['Dispositivos', '161/udp · SNMP'],
          ['Dispositivos', '22/tcp · SSH (solo si no hay SNMP)'],
          ['Controladora UniFi', '443/tcp · API'],
          ['Servidor CMDB', '443/tcp · perfil e ingesta'],
        ]}
      />

      <H>«Solo tengo la API key de UniFi, ¿tengo que instalar algo?»</H>
      <P>
        <strong>No, normalmente no.</strong> Con una credencial de tipo UniFi y un ámbito de tipo
        <strong> Controlador (API)</strong>, «Escanear ahora» se ejecuta en el servidor del CMDB: lee
        los equipos, los clientes y los SSID directamente del controlador. El colector solo hace
        falta si el controlador <strong>no es alcanzable desde el servidor</strong> (por ejemplo, un
        Cloud Key dentro de una red privada sin VPN ni IP pública).
      </P>
      <Note>
        Un colector por empresa/entorno. La credencial del agente fija la empresa: el mismo aparato no
        puede inventariar dos clientes distintos.
      </Note>

      <H>Instalar el colector</H>
      <Steps items={[
        <>Docker (recomendado): copia <code>docker-compose.collector.yml</code>, rellena <code>.env</code> con <code>CMDB_URL</code>, <code>CMDB_COMPANY_SLUG</code> y un token de bootstrap, y ejecuta <code>docker compose -f docker-compose.collector.yml up -d --build</code>.</>,
        <>Linux: <code>sudo ./install_linux.sh --role network --url … --slug … --token …</code>.</>,
        <>Windows: ejecuta el instalador publicado en <strong>Agente → Distribución</strong> con <code>/ROLE=network</code>, o elige «Recolector de red» en la primera pantalla del asistente.</>,
        <>Comprueba que aparece en <strong>Agente → Red → Colectores</strong> en estado <em>online</em>.</>,
      ]} />
      <CommandBlock
        title="Instalación desatendida en Windows"
        command={'CMDBAgentSetup.exe /S /ROLE=network /CMDB_URL=https://cmdb.tu-dominio.com /CMDB_COMPANY_SLUG=tu-empresa /BOOTSTRAP_TOKEN=<token>'}
      />
    </div>
  );
}

function SnmpTab() {
  return (
    <div>
      <P>
        SNMP es la vía universal: sirve para Cisco, Aruba, HP, MikroTik, Juniper, Extreme o
        cualquier switch que hable SNMP. El colector lo usa para leer marca, modelo, número de serie,
        puertos, VLANs, vecinos LLDP, tabla de MACs y estado PoE.
      </P>

      <H>Paso 1 · Habilitar SNMP de solo lectura en el switch</H>
      <P>
        Crea una community o un usuario <strong>de solo lectura</strong>, distinto del de
        administración, y limita el acceso a la IP del colector. En producción no uses <code>public</code>.
      </P>
      <CommandBlock title="Cisco IOS / IOS-XE" command={'snmp-server community CMDB_RO ro 10\naccess-list 10 permit 10.10.0.50\n! SNMPv3\nsnmp-server group CMDB v3 auth read v3read\nsnmp-server user cmdb CMDB v3 auth sha <claveAuth> priv aes 128 <clavePriv>'} />
      <CommandBlock title="Aruba / ProCurve" command={'snmp-server community "CMDB_RO" operator\nsnmpv3 user "cmdb" auth sha <claveAuth> priv aes <clavePriv>'} />
      <CommandBlock title="UniFi (switch o UDM)" command={'Settings → System → SNMP → Enable SNMPv2c\nCommunity: CMDB_RO\n(Si usas la API de UniFi, esto no hace falta.)'} />

      <H>Paso 2 · Crear la credencial en el CMDB</H>
      <Steps items={[
        <>Ve a <strong>Agente → Red → Credenciales → Nueva credencial</strong>.</>,
        <>Tipo <strong>SNMP v2c</strong>: escribe la community, puerto 161, timeout 3 s. Tipo <strong>SNMP v3</strong>: usuario, autenticación (SHA/SHA-256) y privacidad (AES).</>,
        <>La <strong>prioridad</strong> decide el orden de prueba. Pon primero la credencial de los equipos principales.</>,
        <>Guarda. El secreto queda cifrado y no se vuelve a mostrar.</>,
      ]} />

      <H>Paso 3 · Crear el ámbito</H>
      <Steps items={[
        <>En <strong>Ámbitos → Nuevo ámbito</strong>, tipo <strong>Rango (CIDR)</strong>.</>,
        <>Pon el rango de la VLAN de gestión (por ejemplo <code>10.10.0.0/24</code>) y marca la credencial que acabas de crear.</>,
        <>En <strong>Excluir</strong> añade el gateway y lo que no quieras tocar: <code>10.10.0.1</code>, rangos reservados, etc.</>,
        <>Agenda: por defecto cada 1440 minutos (una vez al día). Para la primera prueba déjalo y usa «Escanear ahora».</>,
      ]} />
      <Note>
        El colector prueba los perfiles del ámbito en orden y <strong>recuerda cuál funciona</strong> para
        cada IP. En el siguiente barrido empieza por ese. No hay que asignar comunidad equipo por equipo.
      </Note>

      <H>Paso 4 · Escanear y comprobar</H>
      <Steps items={[
        <>Pulsa <strong>Escanear ahora</strong> y mira <strong>Últimas ejecuciones</strong>: aparecerá en estado <em>running</em> y luego <em>ok</em> o <em>partial</em>.</>,
        <>En <strong>Equipos de Red → Descubiertos</strong> verás cada switch con su modelo, origen (SNMP) y puertos. Al pulsar abre su ficha real.</>,
        <>Los puertos reales aparecen en la ficha del switch; las VLANs en <strong>Redes → VLANs</strong> y las IPs de gestión en <strong>Redes → IPs</strong>.</>,
      ]} />

      <H>Qué esperar de un switch bien configurado</H>
      <P>
        Hostname, fabricante, modelo, <strong>número de serie real del chasis</strong>, firmware, puertos
        con estado y velocidad, VLANs, vecinos LLDP y, si es PoE, qué puertos están alimentando.
      </P>
    </div>
  );
}

function UnifiTab() {
  return (
    <div>
      <Note>
        <strong>Respuesta corta:</strong> con UniFi no necesitas SNMP ni SSH. Necesitas el
        <strong> colector</strong> (rol <code>network</code>) y una única credencial de tipo UniFi con
        la API key del controlador.
      </Note>

      <H>Paso 1 · Crear la API key en el controlador</H>
      <Steps items={[
        <>Entra en el controlador (UDM, UCK, Cloud Key o UniFi OS) como administrador.</>,
        <>Ve a <strong>Settings → Control Plane → API</strong> y pulsa <strong>Create API Key</strong>. Ponle un nombre reconocible («CMDB»).</>,
        <>Copia la clave en ese momento: <strong>solo se muestra una vez</strong>. Si la pierdes, hay que regenerarla.</>,
      ]} />
      <Note tone="warn">
        Trata la API key como una contraseña de administrador: da lectura de toda la red. No la pegues
        en documentación ni en capturas. Este repositorio tuvo una clave real en un documento y hubo
        que rotarla.
      </Note>

      <H>Paso 2 · Crear la credencial en el CMDB</H>
      <Steps items={[
        <>Ve a <strong>Agente → Red → Credenciales → Nueva credencial</strong>.</>,
        <>Tipo <strong>UniFi (API)</strong>.</>,
        <>Host: el del controlador <strong>sin</strong> <code>https://</code> (por ejemplo <code>unifi.tu-dominio.com</code>).</>,
        <>API key: la que acabas de crear. Site: opcional; si lo dejas vacío se recorren todos los sites.</>,
        <>Guarda.</>,
      ]} />
      <Note>
        La API key es <strong>del controlador</strong>, no de cada site ni de cada organización. Con una
        sola clave lees todos los sites que gestiona ese Cloud Key. Lo que sí hay que ajustar por
        cliente es el campo <strong>Site</strong> de la credencial (o del ámbito).
      </Note>

      <H>Paso 3 · Crear el ámbito</H>
      <Steps items={[
        <>En <strong>Ámbitos → Nuevo ámbito</strong>, tipo <strong>Controlador (API)</strong>.</>,
        <>Marca la credencial de UniFi. No hay CIDR: el controlador ya sabe qué equipos tiene.</>,
        <>Agenda: cada 60 minutos es razonable para WiFi y switches gestionados.</>,
      ]} />

      <H>Paso 4 · Escanear y comprobar</H>
      <Steps items={[
        <>Pulsa <strong>Escanear ahora</strong>.</>,
        <>Los <strong>switches</strong> aparecen en <strong>Equipos de Red → Switches</strong>, los <strong>APs</strong> en <strong>WiFi → Puntos de acceso</strong> y el gateway en <strong>WAN → Routers</strong>.</>,
        <>Los puertos (con PoE y velocidad) se ven en la ficha de cada switch; los clientes del controlador se registran como IPs descubiertas.</>,
      ]} />

      <H>Qué se lee del controlador</H>
      <P>
        Nombre, modelo, serie, firmware e IP de cada dispositivo; tabla de puertos; vecinos LLDP; VLANs
        y redes; clientes conectados y su IP. Todo lo que UniFi ya sabe, sin configurar SNMP en cada
        switch.
      </P>

      <H>SSID (redes WiFi) y quién las gestiona</H>
      <P>
        Además de los equipos se leen las <strong>redes WiFi</strong>: nombre (SSID), seguridad,
        VLAN/red, bandas, si está oculta y si está activa. Y lo que de verdad importa aquí:
        si la gestiona el controlador o no.
      </P>
      <Steps items={[
        <>Si el SSID está en la configuración del controlador, se guarda colgando de él
        (<strong>Gestionada: sí</strong>) y todos los APs que la emiten quedan asociados a ese
        controlador.</>,
        <>Si un AP emite un SSID que <em>no</em> aparece en la configuración (alguien configuró el AP
        a mano), se guarda como <strong>No (AP local)</strong>. Eso es precisamente lo que avisa de
        que hay una red fuera del controlador.</>,
        <>Se ven en <strong>Infraestructura → Red → Descubiertos → SSID / WiFi</strong> y también en
        <strong> WiFi → SSID</strong>, junto al resto del inventario.</>,
      ]} />
      <Note tone="warn">
        La contraseña del SSID no se lee nunca: la API del controlador no la devuelve. Solo se guarda
        la configuración visible (seguridad, VLAN, bandas).
      </Note>

      <H>Varios clientes en el mismo Cloud Key</H>
      <P>
        El CMDB asigna cada equipo a la <strong>empresa de la credencial del colector</strong>, no al
        site. El site solo filtra qué lee el controlador. Por eso, con varios clientes en un mismo
        Cloud Key hay dos formas correctas:
      </P>
      <Steps items={[
        <><strong>Un colector por cliente</strong> (recomendado). Cada cliente tiene su empresa en el
        CMDB, su propio colector y una credencial UniFi con el <strong>site de ese cliente</strong>.
        Así no hay forma de que los equipos de un cliente acaben en la empresa de otro.</>,
        <><strong>Un solo colector con todos los sites</strong>, válido solo si esos sites son todos
        del <em>mismo</em> cliente (sus sedes). En ese caso deja el site vacío: se leen todos y se
        guardan en la misma empresa.</>,
      ]} />
      <Note tone="warn">
        Si dejas el site vacío en un Cloud Key con varios clientes, el colector leerá <strong>todos</strong>
        los sites y los guardará en la empresa de su credencial. Usa el botón <strong>Probar</strong> de la
        credencial: lista los sites que ve la API key, con equipos y clientes de cada uno, y avisa si hay
        más de uno sin site configurado.
      </Note>

      <H>Si la API key no funciona</H>
      <Tabla
        rows={[
          ['«API key rechazada» (401/403)', 'Clave caducada, revocada o de otro controlador. Regenera la key y vuelve a guardar la credencial.'],
          ['Error de conexión', 'El colector no llega al host en 443. Prueba `curl -k https://<host>` desde la máquina del colector.'],
          ['Certificado TLS', 'El colector no verifica el certificado por defecto (los UniFi suelen ser autofirmados). No es un error.'],
          ['No aparecen APs', 'Revisa el campo Site: si filtra por un site que no existe, no devuelve dispositivos.'],
        ]}
      />
    </div>
  );
}

function SshTab() {
  return (
    <div>
      <H>Cuándo usar SSH</H>
      <P>
        Solo como respaldo de SNMP. Sirve para equipos que no exponen SNMP pero sí permiten SSH:
        Cisco, Aruba, Fortinet, MikroTik y Ubiquiti. Devuelve identidad y estado de puertos; no lee
        LLDP ni tabla de MACs.
      </P>
      <Steps items={[
        <>Crea un usuario de solo lectura en el equipo (no uses el de administración).</>,
        <>En <strong>Credenciales → Nueva credencial</strong>, tipo <strong>SSH</strong>: usuario, contraseña y modelo de dispositivo (o «Autodetectar»).</>,
        <>Añádela al ámbito <strong>después</strong> de las de SNMP: solo se probará si SNMP no responde.</>,
      ]} />
      <CommandBlock
        title="Comprobación rápida desde el colector"
        command={'nc -vz <switch> 161\nnc -vz <switch> 22\ncurl -sS -I https://<controlador>'}
      />

      <H>Problemas frecuentes</H>
      <Tabla
        rows={[
          ['Todos los equipos con timeout', 'VLAN de gestión sin routing, ACL que bloquea al colector o SNMP deshabilitado.'],
          ['Unos sí y otros no', 'Falta la credencial de esa marca/equipo. Añádela al ámbito y reescanea.'],
          ['El switch sale duplicado', 'Cambió el hostname y no reporta serie. La reconciliación usa serie → MAC → IP → nombre.'],
          ['Los puertos salen vacíos', 'El switch no soporta ifXTable/Q-BRIDGE o SNMP está limitado por ACL. Revisa «skipped_tables» en la ejecución.'],
          ['El barrido no termina', 'Ámbito demasiado grande: baja el máximo de equipos o divídelo en rangos más pequeños.'],
          ['Push en «queued»', 'El colector no llega al CMDB. Guarda en el outbox y reintenta al recuperar la red.'],
        ]}
      />
    </div>
  );
}

export default function AgentNetworkGuide({ onClose }) {
  const [tab, setTab] = useState('start');

  return (
    <Dialog
      title="Cómo configurar el descubrimiento de red"
      icon={<BookOpenIcon style={{ width: 18, height: 18, verticalAlign: '-3px' }} />}
      onClose={onClose}
      maxWidth={880}
      footer={(
        <button type="button" className="list-btn-primary" onClick={onClose}>
          Cerrar
        </button>
      )}
    >
      <div className="dv-tabs-nav" style={{ marginBottom: 14 }}>
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            className={`dv-tab-btn${tab === id ? ' active' : ''}`}
            onClick={() => setTab(id)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Icon style={{ width: 15, height: 15 }} />
            {label}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 6 }}>
        {tab === 'start' && <StartTab />}
        {tab === 'snmp' && <SnmpTab />}
        {tab === 'unifi' && <UnifiTab />}
        {tab === 'ssh' && <SshTab />}
      </div>
    </Dialog>
  );
}
